import { Dialog, EmptyState, Icon, Icons, Intent, List, ListItem } from "construct-ui";
import DivisionModel from "../../models/division/division.model";
import ModelManagementModel from "../../models/model-management/model-management.model";
import ViewBase from "../../views/view-base.view";
import MultiSelectComponent from "../multi-select/multi-select.component";
import paginationComponent from "../pagination/pagination.component";


let uniqueInstance = 1;

/**
 * Select Model Search Input Dialog
 * 
 * Select Model Dialog with filters to search for models.
 * 
 * Supports multiple or single inputs
 * 
 * @author Darrus Goh 
 */
export default function SelectModelSearchInputDialog() {
    ViewBase.call(this);

    const UID = uniqueInstance++;

    const BRAND_SAMSUNG_CODE = '1';
    const KEY_MODEL_CODE = 'modelCode';

    const ID_DIALOG = `SelectModelSearchInputDialog${UID}`;
    const ID_SEARCH_RESULTS = `SelectModelSearchInputDialog-SearchResults${UID}`;
    const ID_SELECT_ALL_CHECKBOX = `SelectModelSearchInputDialog-SelectAllCheckbox${UID}`;

    const DEFAULT_ONCLOSE = () => { console.log('SelectModelSearchInputDialog: onClose()') };
    const DEFAULT_ONSELECT = (selectedItemsItem) => { console.log('SelectModelSearchInputDialog: onSelect(selectedItems | selectedItem)'); };
    const RESULT_LIST_HEIGHT = '50vh';

    const DEFAULT_FILTERS = {
        divisions: [],
        brands: [BRAND_SAMSUNG_CODE],
        modelCode: '',
        modelAlias: ''
    };

    const DEFAULT_PAGINATION = {
        pageIndex: 1,
        pageSize: 10,
        total: 0
    };

    let EULA = false;
    let filters = structuredClone(DEFAULT_FILTERS);
    let pagination = structuredClone(DEFAULT_PAGINATION);

    const API_REQUEST_PARAMS = {
        get division() { return filters.divisions.join('^') },
        get brand() { return filters.brands.join('^') },
        get modelCode() { return filters.modelCode },
        get modelAlias() { return filters.modelAlias },
        get pageIndex() { return pagination.pageIndex },
        get pageSize() { return pagination.pageSize },
        get eulaYn() { return EULA ? 'Y' : 'N' }
    };

    let models = [];
    let divisions = [];
    let brands = [];

    const searchModelDetails = () => {
        this.showLoading();

        models = [];
        return ModelManagementModel.searchModelDetails(API_REQUEST_PARAMS)
            .then(result => {
                if (result != null) {
                    models = result.data;
                    pagination.total = result.total;
                }
            })
            .catch(error => {
                this.handleError(error);
            })
            .finally(() => {
                this.hideLoading();
            });
    }

    const getUserDivisions = () => {
        return DivisionModel.getUserDivisions()
            .then(result => {
                divisions = result;
            })
            .catch(error => {
                this.handleError(error);
            });
    }

    const getBrandListByDivision = () => {
        return DivisionModel.getBrandListByDivision()
            .then(result => {
                brands = result;
            })
            .catch(error => {
                this.handleError(error);
            })
    }

    const onSearch = (multiple, selectedItems) => (e) => {
        e.preventDefault();
        pagination = structuredClone(DEFAULT_PAGINATION);
        document.getElementById(ID_SEARCH_RESULTS).scrollIntoView({ behavior: 'auto', block: 'end' });
        searchModelDetails()
            .then(() => {
                if (multiple) {
                    updateSelectAllCheckbox(selectedItems);
                }
            });

    }

    const onDeselect = (selectedItems, onSelect, deselectItem) => {
        let result = structuredClone(selectedItems);
        const index = result.findIndex((element) => deselectItem.modelCode === element.modelCode);
        if (index >= 0) {
            result.splice(index, 1);
        }

        updateSelectAllCheckbox(result);
        onSelect(result);
    }

    const onDeselectAll = (onSelect) => {
        updateSelectAllCheckbox([]);
        onSelect([]);
    }

    const reset = () => {
        clearFilters();
        pagination = structuredClone(DEFAULT_PAGINATION);
        models = [];
    }

    const clearFilters = () => {
        filters = structuredClone(DEFAULT_FILTERS);
    }

    const isSelected = (selectedItems, modelCode) => {
        return selectedItems.find((model => model.modelCode === modelCode));
    }

    const clickCheckbox = (modelCode) => (e) => {
        e.stopPropagation();
        document.getElementById(modelCode).click();
    };

    const onCheckAll = (selectedItems, onSelect) => (e) => {
        let resultMap = selectedItems.reduce((accumulator, item) => {
            accumulator[item.modelCode] = item;
            return accumulator;
        }, {});

        let checkedInputs = document.querySelectorAll(`#${ID_DIALOG} tbody input:checked`);
        e.target.checked = checkedInputs.length !== models.length;


        models.forEach(model => {
            if (e.target.checked) {
                resultMap[model.modelCode] = model;
            } else {
                delete resultMap[model.modelCode];
            }
        });

        onSelect(Object.values(resultMap));
    };

    const onCheck = (selectedItems, onSelect, selectedItem) => (e) => {
        e.stopPropagation();
        let result = structuredClone(selectedItems);

        if (e.target.checked) {
            result.push(selectedItem);
        } else {
            const index = result.findIndex((model) => model.modelCode === selectedItem.modelCode);
            result.splice(index, 1);
        }

        updateSelectAllCheckbox(result);
        onSelect(result);
    };

    const updateSelectAllCheckbox = (selectedItems) => {
        let resultMap = selectedItems.reduce((accumulator, item) => {
            accumulator[item.modelCode] = item;
            return accumulator;
        }, {});

        let count = 0;
        models.forEach(model => {
            if (resultMap.hasOwnProperty(model.modelCode)) {
                count++;
            }
        });

        let selectAllCheckbox = document.getElementById(ID_SELECT_ALL_CHECKBOX);
        if (count === models.length) {
            selectAllCheckbox.checked = true;
            selectAllCheckbox.indeterminate = false;
        } else if (count === 0) {
            selectAllCheckbox.checked = false;
            selectAllCheckbox.indeterminate = false;
        } else {
            selectAllCheckbox.indeterminate = true;
        }
    };

    const searchViewSingle = (selectedItem, onSelect) => {
        return m(`#${ID_SEARCH_RESULTS}.col`, { style: { height: RESULT_LIST_HEIGHT } },
            m('.card.h-100',
                m('.card-body.h-100',
                    m('.vstack.h-100',
                        m('.table-responsive.h-100',
                            m('table.table.table-hover.table-bordered',
                                m('thead',
                                    m('tr.text-center',
                                        m('td'),
                                        m('th', this.getText('LT00000748', 'Model Code')),
                                        m('th', this.getText('LT00000898', 'Model Alias')),
                                        m('th', this.getText('LT00000241', 'Division')),
                                        m('th', this.getText('LT00000894', 'Brand'))
                                    )
                                ),
                                m('tbody',
                                    models.length > 0
                                        ? models.map(model => m('tr.cursor-pointer', { onclick: clickCheckbox(model.modelCode) },
                                            m('td.text-center',
                                                m(`input#${model.modelCode}`, {
                                                    type: 'checkbox',
                                                    checked: selectedItem?.modelCode === model.modelCode,
                                                    onclick: () => { onSelect(model) }
                                                })
                                            ),
                                            m('td', model.modelCode),
                                            m('td', model.modelAlias),
                                            m('td', model.gscmDivision),
                                            m('td', model.brand)
                                        ))
                                        : m('tr',
                                            m('td', { colspan: 5 }, this.getText("EM00000141", "No records found."))
                                        )
                                )
                            )
                        ),
                        models.length > 0 && m(paginationComponent, {
                            currentPage: pagination.pageIndex,
                            pageSize: pagination.pageSize,
                            totalItems: pagination.total,
                            onPageChanged: (pageIndex) => {
                                pagination.pageIndex = pageIndex;
                                searchModelDetails()
                                    .then(() => { updateSelectAllCheckbox(selectedItems) });
                            },
                            onPageSizeChanged: (pageIndex, pageSize) => {
                                pagination.pageIndex = pageIndex;
                                pagination.pageSize = pageSize;
                                searchModelDetails()
                                    .then(() => { updateSelectAllCheckbox(selectedItems) });
                            }
                        })
                    )
                )
            )
        )
    };

    const searchViewMultiple = (selectedItems, onSelect) => m('.row.g-1',
        m('.col-lg-3.order-lg-2', { style: { height: RESULT_LIST_HEIGHT } },
            m('.card.h-100',
                m('.card-body.h-100',
                    selectedItems.length > 0
                        ? m(List, { class: 'h-100', interactive: true },
                            selectedItems.map(item => m(ListItem, {
                                label: item[KEY_MODEL_CODE],
                                contentRight: m(Icon, {
                                    name: Icons.X,
                                    intent: Intent.NEGATIVE,
                                    onclick: () => { onDeselect(selectedItems, onSelect, item) }
                                }),
                            }))
                        )
                        : m(EmptyState, {
                            class: 'placeholder-text-color',
                            style: { position: 'static' },
                            icon: Icons.DATABASE,
                            content: this.getText('EM00000814', 'No models selected'),
                            fill: true
                        })
                )
            )
        ),
        m('.col-lg-12.order-lg-3',
            m('.hstack.gap-2.justify-content-end',
                m('a.small.cursor-pointer', {
                    onclick: () => {
                        onDeselectAll(onSelect);
                    }
                }, this.getText('LT00000545', 'Clear selections')),
                m('small', `${selectedItems.length} record(s)`)
            )
        ),
        m(`#${ID_SEARCH_RESULTS}.col-lg-9.order-lg-1`, { style: { height: RESULT_LIST_HEIGHT } },
            m('.card.h-100',
                m('.card-body.h-100',
                    m('.vstack.h-100',
                        m('.table-responsive.h-100',
                            m('table.table.table-hover.table-bordered',
                                m('thead',
                                    m('tr.text-center',
                                        m('td',
                                            m('input', {
                                                id: ID_SELECT_ALL_CHECKBOX,
                                                type: 'checkbox',
                                                onclick: onCheckAll(selectedItems, onSelect)
                                            })
                                        ),
                                        m('th', this.getText('LT00000748', 'Model Code')),
                                        m('th', this.getText('LT00000898', 'Model Alias')),
                                        m('th', this.getText('LT00000241', 'Division')),
                                        m('th', this.getText('LT00000894', 'Brand'))
                                    )
                                ),
                                m('tbody',
                                    models.length > 0
                                        ? models.map(model => m('tr.cursor-pointer', { onclick: clickCheckbox(model.modelCode) },
                                            m('td.text-center',
                                                m(`input#${model.modelCode}`, {
                                                    type: 'checkbox',
                                                    checked: isSelected(selectedItems, model.modelCode),
                                                    onclick: onCheck(selectedItems, onSelect, model)
                                                })
                                            ),
                                            m('td', model.modelCode),
                                            m('td', model.modelAlias),
                                            m('td', model.gscmDivision),
                                            m('td', model.brand)
                                        ))
                                        : m('tr',
                                            m('td', { colspan: 5 }, this.getText("EM00000141", "No records found."))
                                        )
                                )
                            )
                        ),
                        models.length > 0 && m(paginationComponent, {
                            currentPage: pagination.pageIndex,
                            pageSize: pagination.pageSize,
                            totalItems: pagination.total,
                            onPageChanged: (pageIndex) => {
                                pagination.pageIndex = pageIndex;
                                searchModelDetails()
                                    .then(() => { updateSelectAllCheckbox(selectedItems) });
                            },
                            onPageSizeChanged: (pageIndex, pageSize) => {
                                pagination.pageIndex = pageIndex;
                                pagination.pageSize = pageSize;
                                searchModelDetails()
                                    .then(() => { updateSelectAllCheckbox(selectedItems) });
                            }
                        })
                    )
                )
            )
        )
    );

    const searchViewFunctions = [searchViewSingle, searchViewMultiple];

    return {
        oninit: ({ attrs: { EULA: _EULA } }) => {
            getUserDivisions();
            getBrandListByDivision();

            EULA = _EULA;
        },
        view: ({ attrs: {
            selectedItems = [],
            selectedItem = null,
            multiple = false,
            isOpen = false,
            onSelect = DEFAULT_ONSELECT,
            onClose = DEFAULT_ONCLOSE } }) => {
            return m(Dialog, {
                class: 'cui-dialog-xl',
                isOpen,
                closeOnOutsideClick: false,
                closeOnEscapeKey: false,
                onClose: onClose,
                onClosed: () => {
                    reset();
                },
                title: this.getText('LT00001183', 'Model Information Search'),
                content: [
                    m(`div#${ID_DIALOG}.vstack.gap-2`,
                        m('.hstack.justify-content-end',
                            m('.d-block.d-sm-block.d-md-none',
                                m('a.btn.btn-primary[data-bs-toggle="collapse"][href="#search-section"][aria-controls="search-section"][aria-expanded="false"]',
                                    m("i.bi.bi-filter"),
                                    this.getText("LT00000232", "Filter")
                                )
                            )
                        ),
                        m("#search-section.collapse.show",
                            m('.card',
                                m('.card-body',
                                    m('form.row.g-2', { onsubmit: onSearch(multiple, selectedItems) },
                                        m('.col-md-3',
                                            m('label.form-label', this.getText('LT00000814', 'GSCM Division(s)')),
                                            m(MultiSelectComponent, {
                                                items: divisions,
                                                keyOnly: true,
                                                selectedItems: filters.divisions,
                                                filterable: false,
                                                onSelect: (selectedDivisions) => {
                                                    filters.divisions = selectedDivisions;
                                                }
                                            })
                                        ),
                                        m('.col-md-3',
                                            m('label.form-label', this.getText('LT00001159', 'Brand(s)')),
                                            m(MultiSelectComponent, {
                                                items: brands,
                                                keyOnly: true,
                                                itemKey: 'brandCode',
                                                itemValue: 'brandName',
                                                selectedItems: filters?.brands,
                                                filterable: false,
                                                onSelect: (selectedBrands) => {
                                                    filters.brands = selectedBrands;
                                                }
                                            })
                                        ),
                                        m('.col-md-3',
                                            m('label.form-label', this.getText('LT00000748', 'Model Code')),
                                            m('input.form-control', {
                                                type: 'text',
                                                maxlength: 30,
                                                value: filters.modelCode,
                                                placeholder: this.getText('LT00000748', 'Model Code'),
                                                onchange: (e) => {
                                                    filters.modelCode = e.target.value;
                                                }
                                            })
                                        ),
                                        m('.col-md-3',
                                            m('label.form-label', this.getText('LT00000898', 'Model Alias')),
                                            m('input.form-control', {
                                                type: 'text',
                                                maxlength: 50,
                                                value: filters.modelAlias,
                                                placeholder: this.getText('LT00000898', 'Model Alias'),
                                                onchange: (e) => {
                                                    filters.modelAlias = e.target.value;
                                                }
                                            })
                                        ),
                                        m('.col-12',
                                            m('.d-flex.gap-2.justify-content-end',
                                                m('button.btn.btn-primary', this.getText("LT00000001", "Search")),
                                                m('button.btn.btn-white', { type: 'button', onclick: clearFilters }, this.getText("LT00000031", "Clear"))
                                            )
                                        )
                                    ),
                                )
                            )
                        ),
                        m('div',
                            searchViewFunctions[+multiple](multiple ? selectedItems : selectedItem, onSelect)
                        )
                    ),
                    this.getBaseViews()
                ],
                footer: m('.hstack.justify-content-end',
                    m('button.btn.btn-white', { onclick: onClose }, this.getText('LT00000021', 'Close'))
                )
            })
        }
    }
}